import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, FileText, Layout, ShoppingBag } from 'lucide-react';

type Duration = '1' | '6' | '12' | 'custom';
type Plan = 'starter' | 'pro' | 'ultra';
type ContentType = 'blog' | 'category' | 'product';

interface ContentTypeQuantity {
  quantity: number;
}

interface SimulatorFormData {
  contentTypes: {
    [key in ContentType]: ContentTypeQuantity;
  };
  name: string;
  email: string;
  message: string;
}

interface PlanDetails {
  price: number;
  credits: number;
  pricePerCredit: number;
  discount: number;
}

interface PricingData {
  [key: string]: {
    [key in Duration]?: PlanDetails;
  };
}

const pricingData: PricingData = {
  starter: {
    '1': { price: 400, credits: 400, pricePerCredit: 1, discount: 0 },
    '6': { price: 1750, credits: 2400, pricePerCredit: 0.729, discount: 27.08 },
    '12': { price: 3360, credits: 4800, pricePerCredit: 0.70, discount: 30 },
  },
  pro: {
    '1': { price: 750, credits: 1000, pricePerCredit: 0.75, discount: 25 },
    '6': { price: 3750, credits: 6000, pricePerCredit: 0.625, discount: 37.5 },
    '12': { price: 7200, credits: 12000, pricePerCredit: 0.60, discount: 40 },
  },
  ultra: {
    '1': { price: 1500, credits: 2000, pricePerCredit: 0.75, discount: 25 },
    '6': { price: 7500, credits: 12000, pricePerCredit: 0.625, discount: 37.5 },
    '12': { price: 14400, credits: 24000, pricePerCredit: 0.60, discount: 40 },
  },
};

const features = [
  'Short and long Blog articles',
  'news and evergreen content',
  'Advanced AI algorithms',
  'E-commerce category and product pages',
  'Audio content for increased engagement',
  'AI-generated realistic images',
  'Social media posts',
  'Deep internal linking',
  'Intelligent netlinking',
  'Advanced SERP analysis',
  'Real-time fact and figure research',
  'Content scoring system',
  'Content versioning',
  'WordPress auto-publishing',
  'Shopify integration (soon)',
  'Bulk content launch',
  'Video triggers (soon)',
  'YouTube channel integration (soon)',
  'RSS feed triggers',
  '3x article regeneration',
  'Multilingual support',
  'Dedicated account manager',
  'Project classification',
  'Persona creation'
];

const CREDIT_COSTS = {
  blog: 30,
  category: 25,
  product: 6
};

const PRODUCT_SETUP_FEE = 2000;

export const PricingSection = () => {
  const { t } = useTranslation();
  const [selectedDuration, setSelectedDuration] = useState<Duration>('1');
  const [formData, setFormData] = useState<SimulatorFormData>({
    contentTypes: {
      blog: { quantity: 0 },
      category: { quantity: 0 },
      product: { quantity: 0 }
    },
    name: '',
    email: '',
    message: ''
  });

  const calculateCredits = () => {
    return Object.entries(formData.contentTypes).reduce((total, [type, data]) => {
      return total + (data.quantity * CREDIT_COSTS[type as ContentType]);
    }, 0);
  };

  const calculatePrice = () => {
    let totalPrice = 0;
    
    Object.entries(formData.contentTypes).forEach(([type, data]) => {
      const credits = data.quantity * CREDIT_COSTS[type as ContentType];
      totalPrice += credits * 0.75; // Base price per credit
      
      if (type === 'product' && data.quantity > 0) {
        totalPrice += PRODUCT_SETUP_FEE; // Add setup fee for product pages
      }
    });

    return totalPrice;
  };

  const handleQuantityChange = (type: ContentType, quantity: number) => {
    setFormData(prev => ({
      ...prev,
      contentTypes: {
        ...prev.contentTypes,
        [type]: { quantity: Math.max(0, quantity) }
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch('https://hook.eu2.make.com/r1tg35oyny7gfqi1rpkj5masxv6r6ts0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          creditsNeeded: calculateCredits(),
          estimatedPrice: calculatePrice(),
          setupFee: formData.contentTypes.product.quantity > 0 ? PRODUCT_SETUP_FEE : 0
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      alert('Your request has been submitted successfully!');
      setFormData({
        contentTypes: {
          blog: { quantity: 0 },
          category: { quantity: 0 },
          product: { quantity: 0 }
        },
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  const renderSimulator = () => (
    <div className="max-w-3xl mx-auto bg-white dark:bg-[#131314] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700/50 p-8">
      <div className="mb-8">
        <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[var(--gradient-start)]" />
          {t('pricing.simulator.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          {t('pricing.simulator.description')}
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* Content Type Selection with Quantities */}
        <div className="space-y-4">
          {[
            { type: 'blog', icon: FileText, label: t('pricing.simulator.types.blog') },
            { type: 'category', icon: Layout, label: t('pricing.simulator.types.category') },
            { type: 'product', icon: ShoppingBag, label: t('pricing.simulator.types.product') }
          ].map(({ type, icon: Icon, label }) => (
            <div
              key={type}
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <div className="flex-shrink-0">
                <Icon className="w-6 h-6 text-[var(--gradient-start)]" />
              </div>
              <div className="flex-grow">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {label}
                </label>
              </div>
              <div className="flex-shrink-0 w-32">
                <input
                  type="number"
                  min="0"
                  value={formData.contentTypes[type as ContentType].quantity}
                  onChange={(e) => handleQuantityChange(type as ContentType, parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Price Summary */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t('pricing.simulator.creditsNeeded')}</span>
            <span className="font-medium">{calculateCredits()} credits</span>
          </div>
          {formData.contentTypes.product.quantity > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t('pricing.simulator.setupFee')}</span>
              <span className="font-medium">€ HT {PRODUCT_SETUP_FEE.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 dark:border-gray-700">
            <span>{t('pricing.simulator.totalPrice')}</span>
            <span className="text-[var(--gradient-start)]">€ HT {calculatePrice().toLocaleString()}</span>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('pricing.simulator.form.name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('pricing.simulator.form.email')}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('pricing.simulator.form.message')}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              rows={4}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      <motion.button
        onClick={handleSubmit}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full bg-gradient-to-br from-[var(--gradient-start)] via-[var(--gradient-middle)] to-[var(--gradient-end)] text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
      >
        {t('pricing.simulator.submit')}
        <ArrowRight className="ml-2 w-4 h-4" />
      </motion.button>
    </div>
  );

  return (
    <section id="pricing" className="section py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
          {t('pricing.title')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </motion.div>

      {/* Duration Tabs */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1">
          {(['1', '6', '12', 'custom'] as Duration[]).map((duration) => (
            <button
              key={duration}
              onClick={() => setSelectedDuration(duration)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedDuration === duration
                ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {duration === 'custom' ? t('pricing.tabs.custom') : t('pricing.tabs.months', { count: parseInt(duration) })}
            </button>
          ))}
        </div>
      </div>

      {selectedDuration !== 'custom' ? (
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {Object.entries(pricingData).map(([plan, durations]) => {
            const details = durations[selectedDuration];
            if (!details) return null;

            return (
              <motion.div
                key={plan}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`relative bg-white dark:bg-[#131314] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700/50 overflow-hidden ${
                  plan === 'pro' ? 'ring-2 ring-[var(--gradient-start)]' : ''
                }`}
              >
                {plan === 'pro' && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)] text-white text-sm font-medium px-4 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}

                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-4 capitalize">
                    {plan}
                  </h3>
                  
                  {details.discount > 0 && (
                    <span className="inline-block bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm font-medium px-3 py-1 rounded-full mb-4">
                      Save {details.discount.toFixed(0)}%
                    </span>
                  )}

                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold">€ HT {details.price.toLocaleString()}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">/ {selectedDuration} month{selectedDuration === '1' ? '' : 's'}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      {details.credits.toLocaleString()} credits included
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      € HT {details.pricePerCredit.toFixed(3)} per credit
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">
                      1 article = 30 credits
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[var(--gradient-start)] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-300 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <motion.a
                    href="https://calendly.com/alex-craft-with-ai/demo-de-craftseo?month=2025-01"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                      plan === 'pro'
                        ? 'bg-gradient-to-br from-[var(--gradient-start)] via-[var(--gradient-middle)] to-[var(--gradient-end)] text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.a>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : renderSimulator()}
    </section>
  );
};